// PublicSurvey.jsx — public-facing response page. No auth required.
// Accessed via /survey/{sid}/{token} link sent to customers.
// v200.53 (2026-08-14) — هجرة من axios المباشر إلى النسخة الواحدة.
// GET/POST كلاهما QUICK_READ (بيانات استبيان قصيرة، بلا LLM ولا تقرير).
// لا تغيير في المنطق ولا الشكل ولا أي رقم.
import React, { useEffect, useState } from "react";
import { api } from "./lib/api";

export const SURVEY_UNAVAILABLE_MSG = "الاستبيان غير متاح أو انتهى";

export function SurveyUnavailable() {
  return (
    <div dir="rtl" data-testid="survey-unavailable" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #f7f0e6 0%, #fef6e9 100%)", fontFamily: "system-ui, -apple-system, 'Tajawal', sans-serif", padding: 16 }}>
      <div style={{ maxWidth: 480, background: "#fff", borderRadius: 16, padding: 28, textAlign: "center", boxShadow: "0 8px 32px rgba(176, 135, 86, 0.15)" }}>
        <div style={{ fontSize: 44, marginBottom: 10 }}>📋</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#b08756" }}>{SURVEY_UNAVAILABLE_MSG}</div>
      </div>
    </div>
  );
}

export default function PublicSurvey({ forcedSid } = {}) {
  const route = forcedSid ? { mode: "open", sid: forcedSid, token: null } : parsePath();
  const { mode, sid, token } = route;  // mode: 'invited' | 'open'
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [preview, setPreview] = useState(false);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [rewardCode, setRewardCode] = useState("");
  const [rewardDesc, setRewardDesc] = useState("");

  useEffect(() => {
    if (!sid) { setErr("الرابط غير صالح"); setLoading(false); return; }
    (async () => {
      try {
        const url = mode === "open"
          ? `/surveys/public-open/${sid}`
          : `/surveys/public/${sid}/${token}`;
        const { data } = await api.get(url, { timeoutClass: "QUICK_READ" });
        setSurvey(data.survey);
        setPreview(!!data.preview);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
        setErr(e?.response?.data?.detail || "لم نتمكن من تحميل الاستبيان");
      } finally { setLoading(false); }
    })();
  }, [sid, token, mode]);

  const setA = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    for (const q of survey.questions) {
      if (q.required) {
        const v = answers[q.key];
        if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) {
          setErr(`السؤال «${q.label}» مطلوب`);
          return;
        }
      }
    }
    setErr(null);
    setSubmitting(true);
    try {
      const url = mode === "open"
        ? `/surveys/public-open/${sid}`
        : `/surveys/public/${sid}/${token}`;
      const { data } = await api.post(url,
        { answers, name: name || undefined, phone: phone || undefined },
        { timeoutClass: "QUICK_READ" });
      setRewardCode(data?.reward_code || "");
      setRewardDesc(data?.reward_description || "");
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر إرسال الردود");
    } finally { setSubmitting(false); }
  };

  const wrap = (children) => (
    <div dir="rtl" style={{ minHeight: "100vh", background: "linear-gradient(135deg, #f7f0e6 0%, #fef6e9 100%)", padding: "40px 16px", fontFamily: "system-ui, -apple-system, 'Tajawal', sans-serif" }}>
      <div style={{ maxWidth: 640, margin: "0 auto", background: "#fff", borderRadius: 16, padding: 28, boxShadow: "0 8px 32px rgba(176, 135, 86, 0.15)" }}>
        {children}
      </div>
    </div>
  );

  if (loading) return wrap(<div style={{ textAlign: "center", color: "#888" }}>جارٍ التحميل…</div>);
  if (notFound && !survey) return <SurveyUnavailable />;
  if (err && !survey) return wrap(<div style={{ textAlign: "center", color: "#b9533e", fontSize: 16 }}>⚠ {err}</div>);
  if (done) return wrap(
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 56, marginBottom: 14 }}>🎉</div>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 10 }}>شكراً لرأيك!</h1>
      <p style={{ color: "#666", lineHeight: 1.8, marginBottom: rewardCode ? 18 : 0 }}>وصلتنا إجاباتك بسلام. ملاحظاتك تساعدنا على تقديم تجربة أفضل في زيارتك القادمة ☕</p>
      {rewardCode && (
        <div data-testid="reward-card" style={{ marginTop: 14, padding: 18, background: "linear-gradient(135deg, #fff7e8 0%, #fef0d3 100%)", border: "2px dashed #d4a574", borderRadius: 12 }}>
          <div style={{ fontSize: 12, color: "#a36b3d", fontWeight: 700, marginBottom: 6 }}>🎁 هديتك</div>
          {rewardDesc && <div style={{ fontSize: 14, color: "#444", marginBottom: 10, lineHeight: 1.7 }}>{rewardDesc}</div>}
          <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>أبرز هذا الكود للكاشير عند زيارتك القادمة:</div>
          <div style={{ fontSize: 30, fontWeight: 900, letterSpacing: 4, color: "#b08756", fontFamily: "monospace", padding: "10px 0" }}>{rewardCode}</div>
        </div>
      )}
    </div>
  );

  return wrap(
    <div data-testid="public-survey-page">
      {preview && (
        <div data-testid="survey-draft-preview-banner" style={{ marginBottom: 14, padding: 10, borderRadius: 8, background: "#FEF3C7", color: "#92400E", fontSize: 13, fontWeight: 700, textAlign: "center" }}>
          معاينة مسودة — تظهر لك لأنك مسجّل كمالك/أدمن؛ الزوار يرون «{SURVEY_UNAVAILABLE_MSG}».
        </div>
      )}
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, color: "#b08756" }}>{survey.title}</h1>
      {survey.description && <p style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>{survey.description}</p>}
      {survey.intro && <div style={{ fontSize: 14, lineHeight: 1.8, color: "#444", padding: 12, background: "#faf4ea", borderRadius: 8, marginBottom: 18 }}>{survey.intro}</div>}

      {mode === "open" && (
        <div style={{ marginBottom: 18, padding: 14, background: "#fdfaf5", borderRadius: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>قبل أن نبدأ — حدّثنا عن نفسك (اختياري)</div>
          <input type="text" placeholder="اسمك" value={name} onChange={(e) => setName(e.target.value)} data-testid="open-name"
            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5d9c2", marginBottom: 8, direction: "rtl", fontFamily: "inherit" }} />
          <input type="tel" placeholder="جوالك (لإشعارك بهديتك)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="open-phone"
            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5d9c2", direction: "rtl", fontFamily: "inherit" }} />
        </div>
      )}

      {survey.questions.map((q, i) => (
        <div key={q.key} style={{ marginBottom: 18, padding: 14, background: "#fdfaf5", borderRadius: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, lineHeight: 1.6 }}>
            {i + 1}. {q.label} {q.required && <span style={{ color: "#b9533e", fontSize: 12 }}>*</span>}
          </div>
          <QInput q={q} value={answers[q.key]} onChange={(v) => setA(q.key, v)} />
        </div>
      ))}

      {err && <div style={{ color: "#b9533e", fontSize: 13, marginBottom: 10, textAlign: "center" }}>⚠ {err}</div>}

      <button
        onClick={submit}
        disabled={submitting || preview}
        data-testid="public-submit"
        style={{
          width: "100%", padding: "14px 20px", borderRadius: 10, border: "none",
          background: "linear-gradient(90deg, #b08756 0%, #d4a574 100%)", color: "#fff",
          fontWeight: 800, fontSize: 16, cursor: submitting ? "wait" : "pointer",
          opacity: submitting ? 0.6 : 1, boxShadow: "0 4px 12px rgba(176, 135, 86, 0.3)",
        }}>
        {submitting ? "جارٍ الإرسال…" : "📩 إرسال الردود"}
      </button>
      <div style={{ marginTop: 18, textAlign: "center", fontSize: 11, color: "#aaa" }}>
        Powered by Thawany Insights ☕
      </div>
    </div>
  );
}

function parsePath() {
  if (typeof window === "undefined") return { mode: null, sid: null, token: null };
  const p = window.location.pathname;
  // /survey/open/{sid}
  let m = p.match(/^\/survey\/open\/([^/]+)\/?$/);
  if (m) return { mode: "open", sid: m[1], token: null };
  // /survey/{sid}/{token}
  m = p.match(/^\/survey\/([^/]+)\/([^/]+)\/?$/);
  if (m) return { mode: "invited", sid: m[1], token: m[2] };
  return { mode: null, sid: null, token: null };
}

function QInput({ q, value, onChange }) {
  const base = { width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5d9c2", fontSize: 14, fontFamily: "inherit", outline: "none", direction: "rtl" };
  if (q.type === "rating") {
    return (
      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)} data-testid={`q-${q.key}-${n}`}
            style={{ width: 50, height: 50, borderRadius: "50%", border: "1px solid #e5d9c2",
              background: value === n ? "#b08756" : "#fff", color: value === n ? "#fff" : "#b08756",
              fontWeight: 800, fontSize: 18, cursor: "pointer" }}>{n}</button>
        ))}
      </div>
    );
  }
  if (q.type === "nps") {
    return (
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "center" }}>
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)} data-testid={`q-${q.key}-${n}`}
            style={{ width: 38, height: 38, borderRadius: 8, border: "1px solid #e5d9c2",
              background: value === n ? "#b08756" : "#fff", color: value === n ? "#fff" : "#444",
              fontWeight: 700, fontSize: 14, cursor: "pointer" }}>{n}</button>
        ))}
      </div>
    );
  }
  if (q.type === "single_choice") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {(q.options || []).map((o) => (
          <label key={o} style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, background: value === o ? "#f5ebd9" : "#fff", borderRadius: 6, cursor: "pointer", border: "1px solid #e5d9c2" }}>
            <input type="radio" name={q.key} checked={value === o} onChange={() => onChange(o)} data-testid={`q-${q.key}-${o}`} />
            <span>{o}</span>
          </label>
        ))}
      </div>
    );
  }
  if (q.type === "multi_choice") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {(q.options || []).map((o) => (
          <label key={o} style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, background: arr.includes(o) ? "#f5ebd9" : "#fff", borderRadius: 6, cursor: "pointer", border: "1px solid #e5d9c2" }}>
            <input type="checkbox" checked={arr.includes(o)} onChange={() => toggle(o)} data-testid={`q-${q.key}-${o}`} />
            <span>{o}</span>
          </label>
        ))}
      </div>
    );
  }
  if (q.type === "textarea") {
    return <textarea rows={4} style={base} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`q-${q.key}`} />;
  }
  return <input type="text" style={base} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`q-${q.key}`} />;
}
